import {Helmet} from '@dr.pogodin/react-helmet';
import {
  Card,
  DataStatus,
  Form,
  FormField,
  FormSubmitHandler,
  showNotification,
} from 'platform/components';
import {Center, VStack} from 'platform/foundation';

import {isNotNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {
  CreateInterestApiArg,
  useCreateInterestMutation,
  useInterestCatalogueQuery,
} from '@dms/api/finance';
import i18n from '@dms/i18n';
import {interestRoutes, testIds} from '@dms/routes';
import {
  CustomerWidgetCard,
  EntityHeader,
  handleApiError,
  Page,
  queryParams,
  Section,
  useCodeList,
} from '@dms/shared';

import {composePath, useNavigate, useQueryState} from 'shared/web';

import {
  InterestCreateFormSchemaType,
  interestCreateFormSchema,
} from './schemas/InterestCreateFormSchema';

// composePath keeps the current query, so the branch confirmed on the vehicle card is dropped on
// the way to the created interest instead of travelling on to the next flows
const WITHOUT_CONFIRMED_BRANCH = {[queryParams.BRANCH_ID]: undefined};

export function InterestCreate() {
  const navigate = useNavigate();

  const [customerId] = useQueryState(queryParams.CUSTOMER_ID);
  const [vehicleId] = useQueryState(queryParams.VEHICLE_ID);
  const [branchId] = useQueryState(queryParams.BRANCH_ID);

  const [createInterest, {isLoading: isCreateInterestLoading}] = useCreateInterestMutation();
  const {isLoading: isInterestCatalogueLoading, isError: isInterestCatalogueErrored} =
    useInterestCatalogueQuery();

  const [interestSourcesCodelist] = useCodeList('interest_source');

  const navigateToInterestDetail = (interestId: string) => {
    navigate(
      composePath(interestRoutes.detailCustomer, {
        params: {
          id: interestId,
        },
        queryParams: WITHOUT_CONFIRMED_BRANCH,
      })
    );
  };

  const handleSubmit: FormSubmitHandler<InterestCreateFormSchemaType> = async (values) => {
    const args: CreateInterestApiArg = {
      createInterestRequestBody: {
        customerId: values.customer?.id ?? null,
        sourceCodeId: values.sourceCodeId[0],
        minipurchaseVehicleId: vehicleId ?? null,
      },
      // The branch confirmed on the vehicle card; other entry points keep the selected branch
      xBranch: isNotNil(vehicleId) ? (branchId ?? undefined) : undefined,
    };

    await createInterest(args)
      .unwrap()
      .then((interest) => {
        if (!interest?.id) {
          showNotification.error();
          return;
        }

        navigateToInterestDetail(interest.id);
      })
      .catch(handleApiError);
  };

  return (
    <>
      <Helmet title={i18n.t('page.interestConcept.title')} />
      <Form<InterestCreateFormSchemaType>
        experimentalZodSchema={interestCreateFormSchema}
        onSubmit={handleSubmit}
        defaultValues={{
          customer: customerId ? {id: customerId} : null,
        }}
      >
        {(control, formApi) => (
          <DataStatus
            data-testid={testIds.interest.create('createInterest')}
            isLoading={isInterestCatalogueLoading}
            isError={isInterestCatalogueErrored}
            minHeight={200}
          >
            <Page
              data-testid={testIds.interest.create('createInterest')}
              isFullHeight
              header={
                <EntityHeader
                  variant="create"
                  data-testid={testIds.interest.create('header')}
                  title={i18n.t('entity.interest.labels.newInterest')}
                  actions={[
                    {
                      'data-testid': testIds.interest.create('header-discard'),
                      type: 'button',
                      variant: 'secondary',
                      title: i18n.t('general.actions.discard'),
                      isDisabled: isCreateInterestLoading,
                      onClick: () => navigate(interestRoutes.overview),
                    },
                    {
                      'data-testid': testIds.interest.create('header-create'),
                      type: 'form-button',
                      variant: 'primary',
                      title: i18n.t('general.actions.create'),
                      control,
                      isDisabled: isNilOrEmpty(formApi.watch('sourceCodeId')),
                      isLoading: isCreateInterestLoading,
                      buttonType: 'submit',
                    },
                  ]}
                  flags={[
                    {label: i18n.t('entity.interest.labels.concept'), colorScheme: 'neutral'},
                  ]}
                  icon="content/next_week"
                />
              }
            >
              <Section>
                <Center>
                  <VStack spacing={4} maxWidth={166} width="100%">
                    <Card title={i18n.t('entity.interest.labels.interest')}>
                      <VStack spacing={4}>
                        <FormField
                          data-testid={testIds.interest.create('source')}
                          control={control}
                          label={i18n.t('entity.interest.labels.source')}
                          name="sourceCodeId"
                          type="chips"
                          options={
                            interestSourcesCodelist?.map((item) => ({
                              label: item.name,
                              value: item.codeId,
                            })) ?? []
                          }
                        />
                      </VStack>
                    </Card>
                    <CustomerWidgetCard
                      data-testid={testIds.interest.create('customer')}
                      isContactPersonHidden
                      canChangeCustomer
                      secondStepComponentType="BUSINESS_CASE"
                      shouldWarnBeforeActiveBusinessCaseEdit
                      customerId={formApi.watch('customer')?.id}
                      onChange={() => formApi.setValue('customer', null)}
                      onCustomer={(customer) => formApi.setValue('customer', customer)}
                    />
                  </VStack>
                </Center>
              </Section>
            </Page>
          </DataStatus>
        )}
      </Form>
    </>
  );
}
